/*
 * pdp-personalisation.js
 * -------------------------------------------------------------------------
 * PRODUCT-PAGE half of the "Name to be printed" guard. The cart-page half
 * lives in assets/cart-personalisation.js, which is only loaded on the cart -
 * which is why the product page had no validation at all.
 *
 * WHICH PRODUCTS GET VALIDATED
 * -------------------------------------------------------------------------
 * King (ymq) Product Options is the single source of truth. On a personalised
 * product it renders:
 *
 *   <div class="ymq-options-box"
 *        data-validate="required|minchar:1|maxchar:20"
 *        data-label="Name to be printed"
 *        data-name="properties[Name to be printed]"> ... <input class="ymq-input-text">
 *
 * On a product that needs no name - a book, say - King renders NOTHING: zero
 * .ymq-options-box elements. So this script keys off King's own markup and is
 * a complete no-op on non-personalised products. There is no hardcoded product
 * or collection list here to drift out of sync with King's admin.
 *
 * Only boxes whose data-validate contains "required" are enforced; an optional
 * King field (e.g. a gift message) is left alone.
 *
 * TIMING - the thing that makes this subtle
 * -------------------------------------------------------------------------
 * King renders its options ASYNCHRONOUSLY, well after DOMContentLoaded. This
 * script is deferred, so when it first runs a personalised product looks
 * exactly like a non-personalised one: zero boxes. Any "nothing here, give up"
 * shortcut at startup therefore disables the guard permanently. The observer is
 * always attached for that reason; the gate itself no-ops while no boxes exist.
 *
 * WHAT IT GUARDS
 * -------------------------------------------------------------------------
 *   Add to cart      -> intercept the click, show an inline error, focus field.
 *                      (Deliberately NOT dimmed: a greyed-out ATC with no
 *                      explanation reads as a broken page.)
 *   Buy it now /     -> GATED (unclickable + dimmed) while a required field is
 *   express /           empty, AND click-intercepted as a backstop. These skip
 *   Razorpay /          the cart entirely and can render inside an iframe,
 *   Addly upsell        where a click listener cannot reach them.
 * -------------------------------------------------------------------------
 */
(function () {
  var BOX_SEL = '.ymq-options-box[data-validate*="required"]';
  var FIELD_SEL = 'input.ymq-input-text, textarea.ymq-input-text, [name^="ymq["]';
  var ATC_SEL = 'button[name="add"], .product-form__submit';
  // Paths that bypass the cart. Gated rather than click-intercepted.
  // Razorpay's REAL trigger on the PDP is .magic-buy-btn (confirmed in the
  // rendered theme). It does NOT contain the string "magic-checkout", so a
  // [class*="magic-checkout"] selector misses it entirely.
  var BYPASS_SEL = [
    '.magic-buy-btn',                 // Razorpay Magic Checkout "Buy Now" (PDP)
    '.magic-buy-btn__parent',
    '.shopify-payment-button',        // native dynamic checkout (Magic adds .magic-hide)
    '.shopify-payment-button__button',
    '.additional-checkout-buttons',
    '.addly_bundle_addToCart',
    '.addly_quickView_addToCart',
    '.n9-fbt-btn-atc',
    '.n9-quickview-btn-custom'
  ].join(',');

  // NEVER gate these. They are the Razorpay PORTAL itself - modal, backdrop,
  // iframe - plus its config/script containers. They are not triggers, and
  // dimming or disabling them would leave a customer stranded inside a checkout
  // that had legitimately opened. A broad [class*="razorpay"] selector hits all
  // three, which is why this exclusion exists.
  var PORTAL_SEL = [
    '.razorpay-container',
    '.razorpay-backdrop',
    '.razorpay-checkout-frame',
    '#magic-checkout-data-container',
    '#magic-merchant-js'
  ].join(',');

  function boxes() {
    return Array.prototype.slice.call(document.querySelectorAll(BOX_SEL));
  }

  function fieldOf(box) {
    return box.querySelector(FIELD_SEL);
  }

  function labelOf(box) {
    return box.getAttribute('data-label') || 'this field';
  }

  // Every required King field that is still empty.
  function missing() {
    return boxes().filter(function (b) {
      var f = fieldOf(b);
      return f && !String(f.value || '').trim();
    });
  }

  // ---- inline error -------------------------------------------------------
  function errorEl(box) {
    var el = box.querySelector('[data-pdp-cp-error]');
    if (!el) {
      el = document.createElement('div');
      el.setAttribute('data-pdp-cp-error', '');
      el.style.cssText =
        'color:#d72c0d;font-size:0.85rem;margin-top:0.35rem;line-height:1.3;';
      box.appendChild(el);
    }
    return el;
  }

  function showError(box, on) {
    var el = errorEl(box);
    if (on) {
      el.textContent = 'Please add “' + labelOf(box) + '” before continuing.';
      el.hidden = false;
      var f = fieldOf(box);
      if (f) f.style.borderColor = '#d72c0d';
    } else {
      el.hidden = true;
      var g = fieldOf(box);
      if (g) g.style.borderColor = '';
    }
  }

  // ---- gating for cart-bypassing controls ---------------------------------
  var GATE = 'data-pdp-cp-gated';

  function gateEl(el, off) {
    if (!el) return;
    if (off) {
      if (!el.hasAttribute(GATE)) {
        el.setAttribute(GATE, '1');
        el.dataset.pdpPrevPe = el.style.pointerEvents || '';
        el.dataset.pdpPrevOp = el.style.opacity || '';
      }
      el.style.pointerEvents = 'none';
      el.style.opacity = '0.4';
      el.setAttribute('aria-disabled', 'true');
    } else if (el.hasAttribute(GATE)) {
      el.style.pointerEvents = el.dataset.pdpPrevPe || '';
      el.style.opacity = el.dataset.pdpPrevOp || '';
      el.setAttribute('aria-disabled', 'false');
      el.removeAttribute(GATE);
      delete el.dataset.pdpPrevPe;
      delete el.dataset.pdpPrevOp;
    }
  }

  function syncGates() {
    if (!boxes().length) return;              // not a personalised product (yet)
    var off = missing().length > 0;
    Array.prototype.slice.call(document.querySelectorAll(BYPASS_SEL)).forEach(function (el) {
      // Never touch the portal (see PORTAL_SEL).
      if (el.matches(PORTAL_SEL) || el.closest(PORTAL_SEL)) return;
      gateEl(el, off);
    });
  }

  var timer = null;
  function scheduleSync() {
    if (timer) return;
    timer = setTimeout(function () { timer = null; syncGates(); }, 120);
  }

  // ---- events -------------------------------------------------------------
  document.addEventListener('input', function (e) {
    if (!e.target.closest) return;
    var box = e.target.closest(BOX_SEL);
    if (!box) return;
    showError(box, false);
    syncGates();
  });

  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;
    var btn = e.target.closest(ATC_SEL);
    if (!btn) return;
    if (!boxes().length) return;              // non-personalised product: never block

    var miss = missing();
    if (!miss.length) return;                 // all good - let Shopify handle it

    e.preventDefault();
    e.stopPropagation();
    boxes().forEach(function (b) { showError(b, false); });
    miss.forEach(function (b) { showError(b, true); });
    syncGates();
    var f = fieldOf(miss[0]);
    if (f) {
      f.scrollIntoView({ behavior: 'smooth', block: 'center' });
      f.focus();
    }
  }, true);

  // Second line of defence for the cart-bypassing controls: if the gate has not
  // landed yet (King rendered a split second ago), still block the click.
  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;
    if (e.target.closest(PORTAL_SEL)) return;        // never touch an open portal
    var btn = e.target.closest(BYPASS_SEL);
    if (!btn) return;
    if (!boxes().length) return;
    var miss = missing();
    if (!miss.length) return;
    e.preventDefault();
    e.stopPropagation();
    miss.forEach(function (b) { showError(b, true); });
    syncGates();
    var f = fieldOf(miss[0]);
    if (f) { f.scrollIntoView({ behavior: 'smooth', block: 'center' }); f.focus(); }
  }, true);

  // Enter-key / programmatic submit of the product form.
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.matches) return;
    if (!form.matches('form[action*="/cart/add"], form[data-type="add-to-cart-form"]')) return;
    if (!boxes().length) return;
    var miss = missing();
    if (!miss.length) return;
    e.preventDefault();
    e.stopPropagation();
    miss.forEach(function (b) { showError(b, true); });
    var f = fieldOf(miss[0]);
    if (f) { f.scrollIntoView({ behavior: 'smooth', block: 'center' }); f.focus(); }
  }, true);

  function init() {
    // NB: do NOT early-return when there are no boxes yet. King renders its
    // options ASYNCHRONOUSLY, well after DOMContentLoaded, so at init time a
    // personalised product looks identical to a non-personalised one. Bailing
    // here meant the observer never attached and the gate never ran - Buy Now
    // stayed live for the whole session. syncGates() is itself a no-op while
    // there are no boxes, so it is safe to always attach.
    syncGates();
    try {
      // King re-renders its options on variant change, and the express buttons
      // mount late, so re-apply on DOM change. Debounced.
      new MutationObserver(scheduleSync)
        .observe(document.documentElement, { childList: true, subtree: true });
    } catch (e) { /* click guards still apply */ }
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
